import type { PayloadAction } from "@reduxjs/toolkit";
import { createSelector, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "~redux/store";
import type {
  EdstFpData,
  FavCrossingTimeRecord,
  FlightplanId,
  TrajectoryUpdatePayload,
} from "@poscon/shared-types/eram";
import { FlightplanTrackPosition, MtcaConflict, TrajectoryPoint } from "@poscon/shared-types";

export type EramTrajectoryData = {
  points: Array<TrajectoryPoint>;
  favCrossings: Array<FavCrossingTimeRecord>;
};

export type EdstFlightplanSlice = {
  aclAckList: FlightplanId[];
  aclUnackList: FlightplanId[];
  aclSpaList: FlightplanId[];
  depAckList: FlightplanId[];
  depUnackList: FlightplanId[];
  depSpaList: FlightplanId[];
  edstFlightplans: Record<FlightplanId, EdstFpData>;
  trajectories: Record<FlightplanId, EramTrajectoryData>;
  fpTrackPositions: Record<FlightplanId, FlightplanTrackPosition>;
  mtcaConflicts: MtcaConflict[];
  forceShowConflicts: Record<FlightplanId, Array<FlightplanId>>;
};

const initialState: EdstFlightplanSlice = {
  aclAckList: [],
  aclUnackList: [],
  aclSpaList: [],
  depAckList: [],
  depUnackList: [],
  depSpaList: [],
  edstFlightplans: {},
  trajectories: {},
  fpTrackPositions: {},
  mtcaConflicts: [],
  forceShowConflicts: {},
};

const edstDataSlice = createSlice({
  name: "edstFlightplans",
  initialState,
  reducers: {
    setAclAckList(state, action: PayloadAction<FlightplanId[]>) {
      state.aclAckList = action.payload;
    },
    setAclUnackList(state, action: PayloadAction<FlightplanId[]>) {
      state.aclUnackList = action.payload;
    },
    setAclSpaList(state, action: PayloadAction<FlightplanId[]>) {
      state.aclSpaList = action.payload;
    },
    setDepAckList(state, action: PayloadAction<FlightplanId[]>) {
      state.depAckList = action.payload;
    },
    setDepUnackList(state, action: PayloadAction<FlightplanId[]>) {
      state.depUnackList = action.payload;
    },
    setDepSpaList(state, action: PayloadAction<FlightplanId[]>) {
      state.depSpaList = action.payload;
    },
    setFpTrackPositions(state, action: PayloadAction<Record<FlightplanId, FlightplanTrackPosition>>) {
      state.fpTrackPositions = action.payload;
    },
    updateTrajectories(state, action: PayloadAction<Record<FlightplanId, TrajectoryUpdatePayload>>) {
      for (const [fpId, trajectoryUpdate] of Object.entries(action.payload)) {
        state.trajectories[fpId] = {
          points: trajectoryUpdate.points,
          favCrossings: trajectoryUpdate.favCrossings || [],
        };
        state.fpTrackPositions[fpId] = trajectoryUpdate.fpTrackPos;
      }
    },
    updateEdstFlightplan(
      state,
      action: PayloadAction<{
        fpId: FlightplanId;
        data: EdstFpData;
      }>
    ) {
      state.edstFlightplans[action.payload.fpId] = action.payload.data;
    },
    deleteEdstFps(state, action: PayloadAction<FlightplanId[]>) {
      for (const fpId of action.payload) {
        delete state.edstFlightplans[fpId];
        delete state.trajectories[fpId];
        delete state.fpTrackPositions[fpId];
      }
    },
    setEdstFlightplans(state, action: PayloadAction<Record<FlightplanId, EdstFpData>>) {
      state.edstFlightplans = action.payload;
    },
    updateEdstFlightplans(state, action: PayloadAction<Record<FlightplanId, EdstFpData>>) {
      Object.assign(state.edstFlightplans, action.payload);
    },
    setMtcaConflicts(state, action: PayloadAction<MtcaConflict[]>) {
      state.mtcaConflicts = action.payload;
    },
    setForceShowConflicts(state, action: PayloadAction<{ key: FlightplanId; fpIds: FlightplanId[] }>) {
      const { key, fpIds } = action.payload;
      state.forceShowConflicts[key] = fpIds;
    },
    removeForceShowConflicts(state, action: PayloadAction<FlightplanId>) {
      delete state.forceShowConflicts[action.payload];
    },
  },
});

export const {
  setAclAckList,
  setAclUnackList,
  setDepAckList,
  setDepUnackList,
  setDepSpaList,
  setAclSpaList,
  setEdstFlightplans,
  updateEdstFlightplans,
  deleteEdstFps,
  updateEdstFlightplan,
  setFpTrackPositions,
  updateTrajectories,
  setMtcaConflicts,
  setForceShowConflicts,
  removeForceShowConflicts,
} = edstDataSlice.actions;
export const reducer = edstDataSlice.reducer;

export const aclAckListSelector = (state: RootState) => state.edstData.aclAckList;
export const aclUnackListSelector = (state: RootState) => state.edstData.aclUnackList;
export const aclSpaListSelector = (state: RootState) => state.edstData.aclSpaList;
export const depAckListSelector = (state: RootState) => state.edstData.depAckList;
export const depUnackListSelector = (state: RootState) => state.edstData.depUnackList;
export const depSpaListSelector = (state: RootState) => state.edstData.depSpaList;
export const edstFlightplansSelector = (state: RootState) => state.edstData.edstFlightplans;
export const edstFpSelector = createSelector(
  [(state: RootState) => state.edstData.edstFlightplans, (_: RootState, fpId: string) => fpId],
  (edstFlightplans, fpId) => edstFlightplans[fpId] ?? {}
);
export const trajectoriesSelector = (state: RootState) => state.edstData.trajectories;
export const fpTrajectorySelector = (state: RootState, fpId: FlightplanId) =>
  state.edstData.trajectories[fpId];
export const fpTrackPositionsSelector = (state: RootState) => state.edstData.fpTrackPositions;
export const fpTrackPositionSelector = (state: RootState, fpId: FlightplanId) =>
  state.edstData.fpTrackPositions[fpId];

export const mtcaConflictsSelector = (state: RootState) => state.edstData.mtcaConflicts;

export const forcedConflictsSelector = (state: RootState) => state.edstData.forceShowConflicts;
export const forceGpdDisplayFpSelector = createSelector([forcedConflictsSelector, (_, fpId: FlightplanId) => fpId], (forcedConflicts, fpId) =>
  Object.entries(forcedConflicts).flatMap(([k, fpIds]) => {
    return fpIds.includes(fpId) ? [k] : [];
  })
)

export const mtcaConflictsForFpIdSelector = createSelector(
  [mtcaConflictsSelector, (_: RootState, fpId: FlightplanId) => fpId],
  (conflicts, fpId) => {
    return conflicts.filter((conflict) => conflict.conflictA.fpId === fpId || conflict.conflictB.fpId === fpId);
  }
);

export const mtcaParticipantForFpIdSelector = createSelector(
  [mtcaConflictsSelector, (_: RootState, fpId: FlightplanId) => fpId],
  (conflicts, fpId) => {
    return conflicts.flatMap((conflict) => {
      if (conflict.conflictA.fpId === fpId) {
        return conflict.conflictA;
      } else if (conflict.conflictB.fpId === fpId) {
        return conflict.conflictB;
      } else {
        return null;
      }
    });
  }
);

export const allAclFpIdsSelector = createSelector(
  [aclAckListSelector, aclUnackListSelector, aclSpaListSelector],
  (ack, unack, spa) => {
    return [...ack, ...unack, ...spa];
  }
);

export const gpdAircraftDataSelector = createSelector(
  [fpTrajectorySelector, fpTrackPositionSelector, edstFpSelector, mtcaConflictsForFpIdSelector],
  (trajectory, fpTrack, edstFp, conflicts) => {
    return {
      edstFp,
      trajectory,
      fpTrack,
      conflicts,
    };
  }
);
